import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  BusinessNameInfoBanner,
  Button,
  Icon,
  Label,
  LogoUpload,
  PreviewModal
} from '@components';
import {
  AccountType,
  BRANCH_ORG_NAME_MAX_LENGTH,
  imageInitialState
} from '@constants/generic';
import { nameRegex } from '@constants/regex';
import { MixpanelEventV2, sendMixpanelEventV2 } from '@core/analytics/mixpanel';
import {
  useLazyGetOrganizationListQuery,
  useUpdateOrganizationByIdMutation
} from '@features/authentication/queries/AuthQuery';
import {
  useImageDeleteMutation,
  useImageUploadMutation
} from '@features/group-management/queries/GroupQuery';
import useNotify from '@hooks/notify';
import { setCurrentOrg } from '@layouts/main-header/userSlice';
import { ApiError, ImageForm } from '@types';
import { getNameErrorMessage, isMinLengthSatisfied } from '@utils/generic';
import { capitalizeFirstLetter } from '@utils/stringFormat';
import { isInvalidEntityName } from '@utils/validations';
import { Org } from 'types/organization';

interface EditOrganizationProps {
  imageUrl: string;
  currentOrgName: string;
  currentOrgId: string;
  handleCancel: () => void;
  accountType: AccountType;
}

const EditOrganization = ({
  imageUrl,
  currentOrgName,
  currentOrgId,
  handleCancel,
  accountType
}: EditOrganizationProps) => {
  const [deleteLogo] = useImageDeleteMutation();
  const [updateLogo] = useImageUploadMutation();
  const [updateOrg] = useUpdateOrganizationByIdMutation();
  const [getOrgList] = useLazyGetOrganizationListQuery();
  const { createSuccessAlert, createErrorAlert } = useNotify();
  const dispatch = useDispatch();

  const [orgImage, setOrgImage] = useState<ImageForm>(imageInitialState);
  const [orgName, setOrgName] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [branchLogo, setBranchLogo] = useState<File | undefined | null>(null);
  const [openImageCropModal, setOpenImageCropModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);

  const isSaveDisabled = isInvalidEntityName(orgName);
  const hasChanges =
    orgName !== currentOrgName ||
    orgImage.preview ||
    (imageUrl && !orgImage.image_url && !orgImage.preview);

  const onChangeOrgName = (value: string) => {
    setNameError('');
    setOrgName(value);
  };

  const onBlurOrgName = (value: string) => {
    if (!isMinLengthSatisfied(value)) setNameError(getNameErrorMessage());
  };

  const handleUpdateComplete = async () => {
    const resp = await getOrgList(null);
    if ('data' in resp) {
      const currentOrg = resp.data?.find((org: Org) => org.id === currentOrgId);
      dispatch(setCurrentOrg(currentOrg));
    }
    setIsLoading(false);
    handleCancel();
  };

  const onDeleteOrgLogo = async () => {
    const payload = {
      type: AccountType.ORGANIZATION || AccountType.INDIVIDUAL,
      id: currentOrgId
    };

    const res = await deleteLogo({ body: payload });
    if ('error' in res)
      createErrorAlert(
        accountType === AccountType.INDIVIDUAL
          ? 'Logo delete failed'
          : 'Organization logo delete failed'
      );
    else
      createSuccessAlert(
        accountType === AccountType.INDIVIDUAL
          ? 'Logo deleted successfully'
          : 'Organization logo deleted successfully'
      );

    handleUpdateComplete();
  };

  const onUpdateOrgLogo = async () => {
    if (orgImage?.file) {
      const imageData = new FormData();
      imageData.append('image', orgImage?.file);
      imageData.append('id', currentOrgId);
      imageData.append(
        'type',
        AccountType.ORGANIZATION || AccountType.INDIVIDUAL
      );

      const res = await updateLogo(imageData);

      if ('error' in res) {
        const error = res.error as ApiError;
        createErrorAlert(
          capitalizeFirstLetter(error?.data?.message || '') ||
            (accountType === AccountType.INDIVIDUAL
              ? 'Logo upload failed'
              : 'Organization logo upload failed')
        );
      } else
        createSuccessAlert(
          accountType === AccountType.INDIVIDUAL
            ? 'Logo uploaded successfully'
            : 'Organization logo uploaded successfully'
        );
      handleUpdateComplete();
    }
  };

  const onUpdateOrgName = async () => {
    const payload = {
      body: { name: orgName },
      orgId: currentOrgId
    };

    const res = await updateOrg(payload);

    if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(
        error?.data?.message ||
          (accountType === AccountType.INDIVIDUAL
            ? 'Display name update failed'
            : 'Organization name update failed')
      );
    } else
      createSuccessAlert(
        accountType === AccountType.INDIVIDUAL
          ? 'Display Name updated successfully'
          : 'Organization name updated successfully'
      );

    handleUpdateComplete();
  };

  const handleOrgSave = () => {
    sendMixpanelEventV2(MixpanelEventV2.ORG_LOGO_SAVED);
    setIsLoading(true);

    if (imageUrl && !orgImage.image_url && !orgImage.preview) onDeleteOrgLogo();
    else if (orgImage.preview) onUpdateOrgLogo();

    if (orgName !== currentOrgName) onUpdateOrgName();
  };

  const handleCancelClick = () => {
    sendMixpanelEventV2(MixpanelEventV2.ORG_LOGO_EDIT_CANCELLED);
    handleCancel();
  };

  const handleChangeLogoClick = () => {
    sendMixpanelEventV2(MixpanelEventV2.ORG_LOGO_CHANGE_CLICKED);
  };

  useEffect(() => {
    setOrgImage({
      file: undefined,
      preview: '',
      image_url: imageUrl
    });
  }, [imageUrl]);

  useEffect(() => {
    setOrgName(currentOrgName);
  }, [currentOrgName]);

  const handleLogoAdd = (file: File) => {
    setBranchLogo(file);
  };

  const handleLogoRemove = () => {
    setBranchLogo(null);
  };

  return (
    <div className="flex w-118 flex-col rounded-xl bg-white">
      {!openImageCropModal && (
        <div className="flex items-center justify-between border-b border-cofeeNeutral5 px-6 py-4 text-xl font-semibold text-cofeeNeutral10">
          <div>
            {accountType === AccountType.INDIVIDUAL
              ? 'Account Details'
              : 'Edit organization details'}
          </div>
          <div
            className="cursor-pointer"
            onClick={handleCancelClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleCancelClick();
              }
            }}
            tabIndex={0}
            role="button"
          >
            <Icon
              name="close"
              size="mediumlarge"
              className="text-cofeeNeutral8"
            />
          </div>
        </div>
      )}

      <LogoUpload
        label="branch_logo"
        setOrgImage={setOrgImage}
        handleAdd={handleLogoAdd}
        handleRemove={handleLogoRemove}
        setOpenImageCropModal={setOpenImageCropModal}
        uploadedFile={branchLogo}
        openImageCropModal={openImageCropModal}
        initialPreviewUrl={orgImage.image_url || orgImage.preview}
        orgName={currentOrgName}
        onChangeLogoClick={handleChangeLogoClick}
      />

      {!openImageCropModal && (
        <>
          <div className="px-6 py-4">
            <Label
              label="Business Name"
              className="!mb-1 text-sm font-semibold !text-cofeeNeutral10"
            />
            <textarea
              placeholder={
                accountType === AccountType.INDIVIDUAL
                  ? 'Display Name'
                  : 'Organization Name'
              }
              value={orgName}
              maxLength={BRANCH_ORG_NAME_MAX_LENGTH}
              onBlur={(e) => onBlurOrgName(e.target.value)}
              onChange={(e) => {
                if (nameRegex.test(e.target.value) || !e.target.value) {
                  onChangeOrgName(e.target.value);
                }
              }}
              disabled
              style={{ fieldSizing: 'content' } as React.CSSProperties}
              className={`min-h-12.5 w-full resize-none overflow-hidden rounded-lg border
                          !bg-cofeeNeutral1 px-2 py-[15px] text-sm leading-5 text-content disabled:cursor-not-allowed ${
                            nameError
                              ? '!border-2 !border-error'
                              : 'border-cofeeNeutral4'
                          }`}
            />
            {nameError && (
              <div className="mt-1 text-xs text-error">{nameError}</div>
            )}
            <BusinessNameInfoBanner
              onViewSample={() => setShowPreviewModal(true)}
            />
          </div>

          <div className="flex justify-end gap-4 border-t border-cofeeNeutral4 px-6 py-4">
            <Button
              label="Cancel"
              variant="tertiary"
              handleClick={handleCancelClick}
              className="w-fit min-w-[85px]"
              labelStyle="!text-cofeeNeutral10 !font-medium"
            />

            <Button
              label="Save"
              handleClick={handleOrgSave}
              disabled={isSaveDisabled || !hasChanges}
              loading={isLoading}
              className="w-fit min-w-[85px]"
            />
          </div>
        </>
      )}
      {showPreviewModal && (
        <PreviewModal
          onClose={() => setShowPreviewModal(false)}
          flow={
            accountType === AccountType.INDIVIDUAL
              ? 'individualOnboard'
              : 'orgOnboard'
          }
          analyticsFlow="branch_settings"
        />
      )}
    </div>
  );
};

export default EditOrganization;
